import { useMemo } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import styles from "./SupportTicketsCard.module.css";
import { SupportTicket } from "../../supportTicket/interface/Support.interface";

/** Same mapping as SupportTicketListing table badges */
function SupportTicketsCard({
    supportTickets,
    loading = false,
    hasSelectedLocation = false,
}: {
    supportTickets: SupportTicket[];
    loading?: boolean;
    /** When false, empty state is hidden (e.g. no location selected yet) */
    hasSelectedLocation?: boolean;
}) {
    const getStatusClass = (status: string) => {
        const s = status?.toLowerCase();
        if (s === "open") return styles.statusOpen;
        if (s === "closed") return styles.statusResolved;
        if (s === "re_open") return styles.statusInProgress;
        return "";
    };

    const formatStatusLabel = (status: string) =>
        status
            .replace("_", " ")
            .replace(/\b\w/g, (c: string) => c.toUpperCase());

    const formatDate = (dateString: string) => {
        if (!dateString) return "-";

        return moment(dateString).format("MM-DD-YYYY, h:mm A");
    };

    /* Sort by latest first (updatedAt, fallback to createdAt) */
    const sortedTickets = useMemo(
        () =>
            [...(supportTickets ?? [])].sort((a, b) => {
                const dateA = moment(a.updatedAt || a.createdAt);
                const dateB = moment(b.updatedAt || b.createdAt);
                return dateB.diff(dateA);
            }),
        [supportTickets]
    );

    return (
        <section className={styles.supportTicketsCardWrap}>
            <div className={styles.supportTicketsCardTopBar}>
                <h2>Support Tickets</h2>
                <Link to="/support-ticket" className={styles.viewAll}>View All</Link>
            </div>

            {sortedTickets.length > 0 ? (
                <div className={styles.ticketList}>
                    {sortedTickets.map((ticket, index) => (
                        <div key={index} className={styles.ticketCard}>
                            <div className={styles.ticketHeader}>
                                <span className={styles.ticketId}>{ticket.id}</span>
                                <span
                                    className={`${styles.statusBadge} ${getStatusClass(
                                        ticket.ticketStatus
                                    )}`}
                                >
                                    {formatStatusLabel(ticket.ticketStatus || "")}
                                </span>
                            </div>

                            <p className={styles.ticketTitle}>{ticket.subject}</p>
                            <span className={styles.ticketDate}
                            >{formatDate(ticket.updatedAt) || formatDate(ticket.createdAt)}</span>
                        </div>
                    ))}
                </div>
            ) : hasSelectedLocation && !loading ? (
                <div className={styles.noEvents}>
                    <p>No support tickets</p>
                </div>
            ) : null}
        </section>
    );
}

export default SupportTicketsCard;